import { useCallback, useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const DECK = "rune";
const empty = { name: "", suit: "", number: 1, title: "", image_url: "", meaning_upright: "", meaning_reversed: "", extra: "" };

export default function AdminRune() {
  const [cards, setCards] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/decks/${DECK}/cards`)
      .then((r) => setCards([...r.data].sort((a, b) => a.number - b.number)))
      .catch(() => setCards([]));
  }, []);

  useEffect(load, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name, suit: "", number: c.number, title: c.title || "", image_url: c.image_url || "",
      meaning_upright: c.meaning_upright || "", meaning_reversed: c.meaning_reversed || "", extra: c.extra || "",
    });
    setEditId(c.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, number: Number(form.number) };
      if (editId) await api.put(`/admin/decks/${DECK}/cards/${editId}`, payload);
      else await api.post(`/admin/decks/${DECK}/cards`, payload);
      toast.success("Runa salvata. Le prossime interpretazioni useranno questa versione.");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa Runa dal sacchetto?")) return;
    await api.delete(`/admin/decks/${DECK}/cards/${id}`);
    toast.success("Runa eliminata.");
    load();
  };

  return (
    <div data-testid="admin-rune-section">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="font-display text-2xl text-amber-100">Rune — base di interpretazione</h2>
        <button onClick={openNew} data-testid="rune-card-add-btn" className="gold-btn px-4 py-2 text-xs flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Nuova Runa
        </button>
      </div>
      <p className="text-xs text-purple-300/60 mb-8 flex items-center gap-2" data-testid="rune-internal-note">
        <EyeOff className="w-4 h-4 text-amber-400/70" />
        Base interna del tuo metodo: non viene mai mostrata ai clienti. Le modifiche diventano subito la nuova base delle interpretazioni.
      </p>

      {cards === null ? null : (
        <div className="space-y-2" data-testid="rune-admin-list">
          {cards.map((c) => (
            <div key={c.id} className="mystic-card px-5 py-3.5 flex items-center justify-between gap-3" data-testid={`rune-admin-card-${c.id}`}>
              <p className="text-sm text-purple-100/85 flex items-center gap-3">
                <span className="text-amber-300 text-lg w-6 text-center">{c.title || "◯"}</span>
                <span>
                  <span className="text-amber-300 font-medium mr-2">{c.number}</span>
                  {c.name}
                </span>
              </p>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(c)} data-testid={`rune-card-edit-${c.id}`} className="p-2 text-purple-300/70 hover:text-amber-300 transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(c.id)} data-testid={`rune-card-delete-${c.id}`} className="p-2 text-purple-300/70 hover:text-red-300 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#180a26] border border-amber-500/25 text-purple-50 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="rune-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-amber-100">{editId ? "Modifica Runa" : "Nuova Runa"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs text-purple-300/60 space-y-1.5 block col-span-1">
                Numero
                <input type="number" min="1" max="25" className="input-dark" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="rune-number-input" />
              </label>
              <label className="text-xs text-purple-300/60 space-y-1.5 block col-span-2">
                Glifo (simbolo, facoltativo)
                <input className="input-dark" placeholder="es. ᚠ" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="rune-glyph-input" />
              </label>
            </div>
            <input className="input-dark" placeholder="Nome della Runa (es. Fehu)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rune-name-input" />
            <div>
              <p className="text-xs text-purple-300/60 mb-1.5">Significato DIRITTA</p>
              <textarea className="input-dark min-h-[90px]" value={form.meaning_upright} onChange={(e) => setForm({ ...form, meaning_upright: e.target.value })} data-testid="rune-upright-input" />
            </div>
            <div>
              <p className="text-xs text-purple-300/60 mb-1.5">Significato RIBALTATA (se previsto)</p>
              <textarea className="input-dark min-h-[90px]" value={form.meaning_reversed} onChange={(e) => setForm({ ...form, meaning_reversed: e.target.value })} data-testid="rune-reversed-input" />
            </div>
            <div>
              <p className="text-xs text-purple-300/60 mb-1.5">Regole particolari, combinazioni, eccezioni</p>
              <textarea className="input-dark min-h-[70px]" value={form.extra} onChange={(e) => setForm({ ...form, extra: e.target.value })} data-testid="rune-extra-input" />
            </div>
            <button onClick={save} disabled={!form.name} data-testid="rune-save-btn" className="gold-btn w-full py-3 text-sm disabled:opacity-50">
              Salva Runa
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
